import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './Admin.css';
import './AdminDashboard.css';
import UserDetails from './UserDetails';
import AdvancedUserStats from './AdvancedUserStats';
import UserManagement from './UserManagement';
import CaseManagement from './CaseManagement';
import CasePerformance from './CasePerformance';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { 
  FaUsers, 
  FaBuilding, 
  FaClipboardList, 
  FaCheckCircle, 
  FaMoon, 
  FaSun, 
  FaFileExport, 
  FaSync, 
  FaTachometerAlt, 
  FaUsersCog, 
  FaChartLine,
  FaBell,
  FaSignOutAlt,
  FaFileDownload,
  FaClock,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const DashboardMap = lazy(() => import('./DashboardMap'));
const AdvancedAnalytics = lazy(() => import('./AdvancedAnalytics'));

function Admin() {
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState({
    user_count: 0,
    enterprise_count: 0,
    case_count: 0,
    completed_cases: 0,
    new_cases_week: 0
  });
  const [userStats, setUserStats] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [timeframe, setTimeframe] = useState('week');
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('adminDarkMode') === 'true';
  });
  const [dashboardLayout, setDashboardLayout] = useState('default');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [trendData, setTrendData] = useState({
    labels: [],
    datasets: []
  });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [kpiTargets, setKpiTargets] = useState({
    user_growth: 10,
    case_completion: 75,
    active_users: 50
  });
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [recentActivity, setRecentActivity] = useState([]);
  
  const { isAdmin, isAuthenticated, logout } = useAuth();
  const history = useHistory();
  
  // Appliquer le thème dès le chargement initial
  useEffect(() => {
    document.body.classList.toggle('dark-theme', isDarkMode);
    document.body.classList.toggle('light-theme', !isDarkMode);
    
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      history.push('/login');
      return;
    }

    if (!isAdmin) {
      history.push('/home');
      return;
    }

    fetchData();
    fetchRecentActivity();
    setupWebSocket();
    
    document.body.classList.toggle('dark-theme', isDarkMode);
    document.body.classList.toggle('light-theme', !isDarkMode);
    localStorage.setItem('adminDarkMode', isDarkMode);
    
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [isAuthenticated, isAdmin, history, isDarkMode]);

  useEffect(() => {
    if (refreshInterval) clearInterval(refreshInterval);
    
    if (activeTab === 'dashboard') {
      const interval = setInterval(() => {
        fetchData(true);
        setLastUpdate(new Date());
      }, 60000);
      setRefreshInterval(interval);
    }
    
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [activeTab]);

  useEffect(() => {
    fetchTrendData();
  }, [timeframe]);

  const setupWebSocket = () => {
    try {
      // Vérifier si l'URL WebSocket est disponible
      const wsUrl = process.env.REACT_APP_WS_URL;
      if (!wsUrl) {
        console.log('URL WebSocket non définie, fonctionnement en mode démo');
        return;
      }
      
      const ws = new WebSocket(`${wsUrl}/admin/updates`);
      
      ws.onopen = () => {
        console.log('WebSocket connection established');
        addNotification({
          type: 'success',
          message: 'Connexion temps réel établie',
          timestamp: Date.now()
        });
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'stats_update') {
          setStatistics(prev => ({...prev, ...data.payload}));
          setLastUpdate(new Date());
        } else if (data.type === 'notification') {
          addNotification(data.payload);
          fetchRecentActivity();
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        addNotification({
          type: 'error',
          message: 'Erreur de connexion WebSocket',
          timestamp: Date.now()
        });
      };
      
      return () => {
        ws.close();
      };
    } catch (error) {
      console.error('Failed to setup WebSocket:', error);
      
      // Simuler quelques notifications en mode démo
      setTimeout(() => {
        addNotification({
          type: 'info',
          message: 'Mode démo activé - données générées automatiquement',
          timestamp: Date.now()
        });
      }, 2000);
    }
  };

  const addNotification = (notification) => {
    setNotifications(prev => [
      { id: Date.now(), ...notification },
      ...prev
    ].slice(0, 10));
  };

  const fetchData = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      
      if (!apiUrl) {
        // Mode démo si l'API n'est pas disponible
        console.log('API URL non définie, utilisation des données de démonstration');
        setDemoData();
        if (!silent) setIsLoading(false);
        return;
      }
      
      try {
        const usersResponse = await axios.get(`${apiUrl}/admin/users`, {
          headers: {
            'Authorization': 'Bearer admin',
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          timeout: 5000
        });
        setUsers(usersResponse.data);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
        // Utiliser des données fictives pour les utilisateurs
        setUsers(generateDemoUsers(25));
      }

      try {
        const statsResponse = await axios.get(`${apiUrl}/admin/statistics`, {
          params: { timeframe },
          headers: {
            'Authorization': 'Bearer admin',
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          timeout: 5000
        });
        setStatistics(statsResponse.data);
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
        // Utiliser des données fictives pour les statistiques
        setStatistics(generateDemoStats());
      }

      try {
        const userStatsResponse = await axios.get(`${apiUrl}/api/users/statistics`, {
          params: { timeframe },
          headers: {
            'Authorization': 'Bearer admin',
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          timeout: 5000
        });
        setUserStats(userStatsResponse.data);
      } catch (statsError) {
        console.error('Erreur lors du chargement des statistiques avancées:', statsError);
        // Utiliser des données fictives pour les statistiques utilisateurs
        setUserStats(generateDemoUserStats());
      }

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des données. Mode démo activé.');
      setDemoData();
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      
      if (!apiUrl) {
        // Mode démo 
        setRecentActivity(generateDemoActivity());
        return;
      }
      
      const response = await axios.get(`${apiUrl}/admin/recent-activity`, {
        headers: {
          'Authorization': 'Bearer admin',
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        timeout: 5000
      });
      
      setRecentActivity(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des activités récentes:', error);
      setRecentActivity(generateDemoActivity());
    }
  };

  const generateDemoUsers = (count = 25) => {
    const roles = ['user', 'admin'];
    const users = [];
    
    for (let i = 0; i < count; i++) {
      users.push({
        id: i + 1,
        username: `user${i+1}`,
        email: `user${i+1}@example.com`,
        is_admin: i < 3 ? true : false,
        is_active: Math.random() > 0.2,
        last_login: new Date(Date.now() - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000)),
        login_count: Math.floor(Math.random() * 50)
      });
    }
    
    return users;
  };
  
  const generateDemoStats = () => {
    return {
      user_count: 25,
      enterprise_count: 12,
      case_count: 45,
      completed_cases: 32,
      in_progress_cases: 10,
      new_cases_week: 8,
      completed_cases_week: 6,
      growth_users: 12.5,
      growth_cases: 15.3,
      sectors_data: [
        { name: 'Technologie', cases: 15 },
        { name: 'Santé', cases: 10 },
        { name: 'à‰ducation', cases: 8 },
        { name: 'Finance', cases: 7 },
        { name: 'Autres', cases: 5 }
      ]
    };
  };
  
  const generateDemoUserStats = () => {
    return {
      active_users_count: 18,
      inactive_users_count: 7,
      average_logins_per_week: 4.2,
      user_growth_rate: 15,
      most_active_day: 'Lundi',
      least_active_day: 'Dimanche',
      daily_active_users: [12, 15, 14, 18, 20, 10, 8],
      user_retention_rate: 85
    };
  };
  
  const generateDemoActivity = () => {
    const activities = [
      { type: 'user_login', username: 'martin.dupont', timestamp: Date.now() - 5 * 60 * 1000 },
      { type: 'case_created', username: 'sophie.martin', company: 'TechSolutions', timestamp: Date.now() - 35 * 60 * 1000 },
      { type: 'user_registered', username: 'jean.durand', timestamp: Date.now() - 2 * 60 * 60 * 1000 },
      { type: 'case_completed', username: 'admin', company: 'Santé Plus', timestamp: Date.now() - 5 * 60 * 60 * 1000 },
      { type: 'user_login', username: 'admin', timestamp: Date.now() - 6 * 60 * 60 * 1000 }
    ];
    
    return activities;
  };

  const setDemoData = () => {
    setUsers(generateDemoUsers(25));
    setStatistics(generateDemoStats());
    setUserStats(generateDemoUserStats());
    setRecentActivity(generateDemoActivity());
  };

  const fetchTrendData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      
      if (!apiUrl) {
        setDemoTrendData();
        return;
      }
      
      const response = await axios.get(`${apiUrl}/admin/trends`, {
        params: { timeframe },
        headers: {
          'Authorization': 'Bearer admin',
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        }
      });
      
      setTrendData(response.data);
      
    } catch (error) {
      console.error('Erreur lors du chargement des tendances:', error);
      setDemoTrendData();
    }
  };

  const setDemoTrendData = () => {
    // Générer des données de tendances de démonstration
    const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    const weeks = ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'];
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin'];
    
    let labels = [];
    
    switch(timeframe) {
      case 'week':
        labels = days;
        break;
      case 'month':
        labels = weeks;
        break;
      case 'year':
        labels = months;
        break;
      default:
        labels = days;
    }
    
    setTrendData({
      labels,
      datasets: [
        {
          label: 'Nouveaux utilisateurs',
          data: labels.map(() => Math.floor(Math.random() * 10) + 1),
          borderColor: '#4CAF50',
          backgroundColor: 'rgba(76, 175, 80, 0.2)',
        },
        {
          label: 'Nouveaux cas',
          data: labels.map(() => Math.floor(Math.random() * 15) + 1),
          borderColor: '#2196F3',
          backgroundColor: 'rgba(33, 150, 243, 0.2)',
        }
      ]
    });
  };

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const handleExport = (format) => {
    // Simuler l'exportation de données
    setShowExportMenu(false);
    
    const timestamp = new Date().toISOString().replace(/:/g, '-').slice(0, 19);
    const fileName = `admin-dashboard-export-${timestamp}.${format}`;
    
    addNotification({
      type: 'success',
      message: `Export des données en ${format.toUpperCase()} réussi: ${fileName}`,
      timestamp: Date.now()
    });
    
    // Vous pouvez implémenter l'exportation réelle ici, ou montrer un message de démonstration
    setTimeout(() => {
      alert(`Démonstration: Les données seraient exportées au format ${format.toUpperCase()} dans un fichier nommé ${fileName}`);
    }, 500);
  };
  
  const handleRefresh = () => {
    fetchData();
    fetchRecentActivity();
    fetchTrendData();
    setLastUpdate(new Date());
    
    addNotification({
      type: 'info',
      message: 'Données rafraà®chies avec succès',
      timestamp: Date.now()
    });
  };
  
  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    
    addNotification({
      type: 'info',
      message: `Période modifiée: ${getTimeframeLabel(newTimeframe)}`,
      timestamp: Date.now()
    });
  };
  
  const getTimeframeLabel = (timeframe) => {
    switch(timeframe) {
      case 'week': return 'Semaine';
      case 'month': return 'Mois';
      case 'year': return 'Année';
      default: return 'Semaine';
    }
  };

  const handleLogout = () => {
    logout();
    history.push('/');
  };
  
  const toggleNotificationsPanel = () => {
    setShowNotifications(prev => !prev);
  };
  
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000); // différence en secondes
    
    if (diff < 60) {
      return `il y a ${diff} secondes`;
    } else if (diff < 3600) {
      return `il y a ${Math.floor(diff / 60)} minutes`;
    } else if (diff < 86400) {
      return `il y a ${Math.floor(diff / 3600)} heures`;
    } else {
      return `il y a ${Math.floor(diff / 86400)} jours`;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'users':
        return <UserManagement users={users} onUserUpdated={fetchData} />;
      case 'cases':
        return <CaseManagement />;
      case 'analytics':
        return (
          <Suspense fallback={<div className="admin-loading">Chargement des analyses avancées...</div>}>
            <AdvancedAnalytics statistics={statistics} trendData={trendData} timeframe={timeframe} />
          </Suspense>
        );
      default:
        return renderDashboard();
    }
  };

  const renderDashboard = () => {
    // Format d'affichage pour dernière mise à  jour
    const formatLastUpdate = () => {
      return lastUpdate.toLocaleTimeString('fr-FR');
    };
    
    return (
      <div className="admin-dashboard">
        {/* Contrà´les du tableau de bord */}
        <div className="dashboard-controls">
          <div className="timeframe-selector">
            <button 
              className={`timeframe-btn ${timeframe === 'week' ? 'active' : ''}`}
              onClick={() => handleTimeframeChange('week')}
            >
              <FaClock /> Semaine
            </button>
            <button 
              className={`timeframe-btn ${timeframe === 'month' ? 'active' : ''}`}
              onClick={() => handleTimeframeChange('month')}
            >
              <FaClock /> Mois
            </button>
            <button 
              className={`timeframe-btn ${timeframe === 'year' ? 'active' : ''}`}
              onClick={() => handleTimeframeChange('year')}
            >
              <FaClock /> Année
            </button>
          </div>
          
          <div className="dashboard-actions">
            <div className="last-update">
              Dernière mise à  jour: {formatLastUpdate()}
            </div>
            <button className="theme-toggle" onClick={toggleDarkMode}>
              {isDarkMode ? <FaSun /> : <FaMoon />} {isDarkMode ? 'Mode clair' : 'Mode sombre'}
            </button>
            <button className="refresh-btn" onClick={handleRefresh}>
              <FaSync /> Actualiser
            </button>
            <div className="export-dropdown">
              <button className="export-btn" onClick={() => setShowExportMenu(!showExportMenu)}>
                <FaFileExport /> Exporter
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => handleExport('csv')}>CSV</button>
                  <button onClick={() => handleExport('pdf')}>PDF</button>
                  <button onClick={() => handleExport('json')}>JSON</button>
                </div>
              )}
            </div>
            <button className="notification-bell" onClick={toggleNotificationsPanel}>
              <FaBell />
              {notifications.length > 0 && (
                <span className={`notification-badge ${notifications.length > 0 ? 'has-notifications' : ''}`}>
                  {notifications.length}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Cartes de statistiques */}
        <div className="admin-stats-container">
          <div className="admin-stat-card animated" style={{animationDelay: '0.1s'}}>
            <div className="stat-icon">
              <FaUsers />
            </div>
            <div className="stat-content">
              <h3>Utilisateurs</h3>
              <p className="admin-stat-value">{statistics.user_count || 0}</p>
              {statistics.growth_users && (
                <p className={`stat-trend ${statistics.growth_users >= 0 ? 'positive' : 'negative'}`}>
                  {statistics.growth_users >= 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(statistics.growth_users)}%
                </p>
              )}
            </div>
          </div>
          
          <div className="admin-stat-card animated" style={{animationDelay: '0.2s'}}>
            <div className="stat-icon">
              <FaBuilding />
            </div>
            <div className="stat-content">
              <h3>Entreprises</h3>
              <p className="admin-stat-value">{statistics.enterprise_count || 0}</p>
              <p className="admin-stat-subvalue">Secteurs actifs: {statistics.sectors_data?.length || 0}</p>
            </div>
          </div>
          
          <div className="admin-stat-card animated" style={{animationDelay: '0.3s'}}>
            <div className="stat-icon">
              <FaClipboardList />
            </div>
            <div className="stat-content">
              <h3>Cas d'étude</h3>
              <p className="admin-stat-value">{statistics.case_count || 0}</p>
              {statistics.growth_cases && (
                <p className={`stat-trend ${statistics.growth_cases >= 0 ? 'positive' : 'negative'}`}>
                  {statistics.growth_cases >= 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(statistics.growth_cases)}%
                </p>
              )}
            </div>
          </div>
          
          <div className="admin-stat-card animated" style={{animationDelay: '0.4s'}}>
            <div className="stat-icon">
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h3>Cas complétés</h3>
              <p className="admin-stat-value">{statistics.completed_cases || 0}</p>
              <p className="admin-stat-subvalue">
                {statistics.case_count ? Math.round((statistics.completed_cases / statistics.case_count) * 100) : 0}% du total
              </p>
              
              <div className="kpi-gauge">
                <div 
                  className="kpi-progress" 
                  style={{
                    width: `${statistics.case_count ? Math.min(100, Math.round((statistics.completed_cases / statistics.case_count) * 100)) : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Graphiques et visualisations */}
        <div className="dashboard-charts">
          <div className="chart-row">
            <div className="chart-card">
              <h3>Tendances d'activité</h3>
              {trendData.labels && trendData.labels.length > 0 ? (
                <div className="chart-container">
                  <Line 
                    data={trendData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: false,
                          text: 'Tendances',
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="chart-loading">Chargement des données...</div>
              )}
            </div>
            
            <div className="chart-card">
              <h3>Répartition par secteur</h3>
              {statistics.sectors_data && statistics.sectors_data.length > 0 ? (
                <div className="chart-container">
                  <Doughnut 
                    data={{
                      labels: statistics.sectors_data.map(s => s.name),
                      datasets: [
                        {
                          data: statistics.sectors_data.map(s => s.cases),
                          backgroundColor: [
                            'rgba(54, 162, 235, 0.6)',
                            'rgba(255, 99, 132, 0.6)',
                            'rgba(255, 206, 86, 0.6)',
                            'rgba(75, 192, 192, 0.6)',
                            'rgba(153, 102, 255, 0.6)',
                          ],
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'right',
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="chart-loading">Chargement des données...</div>
              )}
            </div>
          </div>
          
          <div className="chart-card full-width">
            <div className="section-header">
              <h3>Activité récente</h3>
              <div className="section-actions">
                <button className="section-action-btn" onClick={fetchRecentActivity}>
                  <FaSync /> Actualiser
                </button>
              </div>
            </div>
            
            {/* Flux d'activité */}
            <div className="admin-activity-feed">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={index} className="admin-activity-card animated" style={{animationDelay: `${index * 0.1}s`}}>
                    <div className={`admin-activity-icon ${activity.type.includes('user') ? 'user-icon' : 'case-icon'}`}>
                      {activity.type.includes('user') ? <FaUsers /> : <FaClipboardList />}
                    </div>
                    <div className="admin-activity-content">
                      <div className="admin-activity-text">
                        {activity.type === 'user_login' && `${activity.username} s'est connecté`}
                        {activity.type === 'user_registered' && `Nouvel utilisateur inscrit: ${activity.username}`}
                        {activity.type === 'case_created' && `${activity.username} a créé un nouveau cas pour ${activity.company}`}
                        {activity.type === 'case_completed' && `${activity.username} a complété le cas de ${activity.company}`}
                      </div>
                      <div className="admin-activity-value">{formatTimeAgo(activity.timestamp)}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-activity">Aucune activité récente à  afficher</div>
              )}
            </div>
          </div>
          
          <div className="chart-row">
            <div className="chart-card">
              <h3>Utilisateurs actifs par jour</h3>
              {userStats && userStats.daily_active_users ? (
                <div className="chart-container">
                  <Bar 
                    data={{
                      labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
                      datasets: [
                        {
                          label: 'Utilisateurs actifs',
                          data: userStats.daily_active_users,
                          backgroundColor: 'rgba(54, 162, 235, 0.6)',
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="chart-loading">Chargement des données...</div>
              )}
            </div>
            
            <div className="chart-card">
              <h3>Statuts des utilisateurs</h3>
              {userStats ? (
                <div className="chart-container">
                  <Pie
                    data={{
                      labels: ['Actifs', 'Inactifs'],
                      datasets: [
                        {
                          data: [
                            userStats.active_users_count || 0, 
                            userStats.inactive_users_count || 0
                          ],
                          backgroundColor: [
                            'rgba(75, 192, 192, 0.6)',
                            'rgba(255, 99, 132, 0.6)'
                          ],
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'right',
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="chart-loading">Chargement des données...</div>
              )}
            </div>
          </div>
        </div>
        
        {/* Panneau de notifications */}
        {showNotifications && (
          <div className="notifications-panel">
            <div className="notifications-header">
              <h3>Notifications</h3>
              <button className="close-notifications" onClick={() => setShowNotifications(false)}>à—</button>
            </div>
            <div className="notifications-list">
              {notifications.length > 0 ? (
                notifications.map(notification => (
                  <div key={notification.id} className={`notification-item ${notification.type}`}>
                    <div className="notification-icon">
                      {notification.type === 'success' && 'âœ“'}
                      {notification.type === 'error' && 'âœ—'}
                      {notification.type === 'warning' && 'âš '}
                      {notification.type === 'info' && 'â„¹'}
                    </div>
                    <div className="notification-content">
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{formatTimeAgo(notification.timestamp)}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-notifications">Aucune notification</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`admin-loading ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <p>Chargement du tableau de bord administrateur...</p>
      </div>
    );
  }

  return (
    <div className={`admin-container ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      <div className="admin-header">
        <h1>Tableau de Bord Administrateur</h1>
        <button className="admin-logout-btn" onClick={handleLogout}>
          <FaSignOutAlt /> Déconnexion
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'dashboard' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <FaTachometerAlt /> Tableau de bord
        </button>
        <button 
          className={`admin-tab ${activeTab === 'users' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FaUsersCog /> Gestion des utilisateurs
        </button>
        <button 
          className={`admin-tab ${activeTab === 'cases' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('cases')}
        >
          <FaClipboardList /> Gestion des cas
        </button>
        <button 
          className={`admin-tab ${activeTab === 'analytics' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <FaChartLine /> Analyses avancées
        </button>
      </div>

      <div className="admin-content">
        {renderTabContent()}
      </div>
    </div>
  );
}

export default Admin; 